import {
  ContractRenewalStateEnum,
  IContractAttachmentData,
  IContractData,
  IContractItemData
} from '@/models/contract';
import { IContractService, IContractFetchParams } from './contract.types';
import { AxiosInstance } from 'axios';
import { IPaginatedListData } from '../types';
import { objectToFormData } from '@/utils/objectToFormData';

export class ContractService implements IContractService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/contracts/';
  }

  fetchContracts(
    params?: IContractFetchParams
  ): Promise<IPaginatedListData<IContractData>> {
    return this.axios
      .get<IPaginatedListData<IContractData>>(`${this.endpoint}`, {
        params
      })
      .then(({ data }) => data);
  }

  fetchContract(id: number): Promise<IContractData> {
    return this.axios
      .get<IContractData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  createContract(contract: IContractData): Promise<IContractData> {
    return this.axios
      .post<IContractData>(`${this.endpoint}`, contract)
      .then(({ data }) => data);
  }

  updateContract(contract: IContractData): Promise<IContractData> {
    return this.axios
      .patch<IContractData>(`${this.endpoint}${contract.id}/`, contract)
      .then(({ data }) => data);
  }

  deleteContract(id: number): Promise<void> {
    return this.axios.delete(`${this.endpoint}${id}/`).then(({ data }) => data);
  }

  updateContractState(id: number, state: number): Promise<IContractData> {
    return this.axios
      .put<IContractData>(`${this.endpoint}${id}/set-state/${state}/`)
      .then(({ data }) => data);
  }

  initiateContractRenewal(id: number): Promise<IContractData> {
    return this.axios
      .put<IContractData>(`${this.endpoint}${id}/initiate-renewal/`)
      .then(({ data }) => data);
  }

  confirmRenewal(
    id: number,
    confirmation_type: ContractRenewalStateEnum
  ): Promise<IContractData> {
    return this.axios
      .post<IContractData>(`${this.endpoint}${id}/confirm-renewal/`, {
        confirmation_type
      })
      .then(({ data }) => data);
  }

  addContractAttachment(
    id: number,
    attachment: IContractAttachmentData
  ): Promise<IContractAttachmentData> {
    const formData = objectToFormData({ attachment });
    return this.axios
      .put<IContractAttachmentData>(
        `${this.endpoint}${id}/add-attachment/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  removeContractAttachment(id: number, attachmentId: number): Promise<void> {
    return this.axios
      .put<void>(`${this.endpoint}${id}/remove-attachment/`, {
        attachment: attachmentId
      })
      .then(({ data }) => data);
  }

  downloadContractAttachment(id: number, attachmentId: number) {
    return this.axios
      .get<Blob>(`${this.endpoint}${id}/download-attachment/${attachmentId}/`, {
        responseType: 'blob'
      })
      .then(({ data }) => data);
  }

  fetchContractItems(id: number): Promise<IContractItemData[]> {
    return this.axios
      .get<IContractItemData[]>(`${this.endpoint}${id}/items/`)
      .then(({ data }) => data);
  }

  fetchContractItem(id: number, itemId: number): Promise<IContractItemData> {
    return this.axios
      .get<IContractItemData>(`${this.endpoint}${id}/items/${itemId}/`)
      .then(({ data }) => data);
  }

  createContractItem(
    id: number,
    data: IContractItemData
  ): Promise<IContractItemData> {
    return this.axios
      .post<IContractItemData>(`${this.endpoint}${id}/items/`, data)
      .then(({ data }) => data);
  }

  updateContractItem(
    id: number,
    data: IContractItemData
  ): Promise<IContractItemData> {
    return this.axios
      .patch<IContractItemData>(`${this.endpoint}${id}/items/${data.id}/`, data)
      .then(({ data }) => data);
  }

  deleteContractItem(id: number, itemId: number): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${id}/items/${itemId}/`)
      .then(({ data }) => data);
  }
}
